import { cn } from '@/shadcn-ui/utils';
import { events, Viewer } from '@photo-sphere-viewer/core';
import '@photo-sphere-viewer/core/index.css';
import { useEffect, useRef, useState } from 'react';

interface PhotoSphereProps {
  url: string;
  className?: string;
  /** Intended to communicate when viewer enters fullscreen */
  fullscreenEventDelegate?: (isFullscreen: boolean) => void;
}

export default function PhotoSphere({
  url,
  className,
  fullscreenEventDelegate,
}: PhotoSphereProps) {
  const viewerContainerRef = useRef(null);
  const [viewerInitialized, setViewerInitialized] = useState(false);

  useEffect(() => {
    let viewer: Viewer | null = null;
    const handleFullscreen = (e: events.FullscreenEvent) => {
      if (e.type === 'fullscreen') {
        fullscreenEventDelegate?.(e.fullscreenEnabled);
      }
    };

    if (viewerContainerRef.current && !viewerInitialized) {
      /** Docs to config: https://photo-sphere-viewer.js.org/guide/config.html */
      viewer = new Viewer({
        container: viewerContainerRef.current,
        panorama: url,
        navbar: ['zoom', 'move', 'fullscreen'],
        sphereCorrection: { pan: -0.5, tilt: 0, roll: 0 }, // Position viewport towards steering wheel
      });

      setViewerInitialized(true);

      viewer.addEventListener(events.FullscreenEvent.type, handleFullscreen);
    }

    return () => {
      if (viewerContainerRef.current && viewerInitialized) {
        viewer?.removeEventListener(events.FullscreenEvent.type, handleFullscreen);
        viewerContainerRef.current = null;
      }
    };
  }, [fullscreenEventDelegate, url, viewerInitialized]);

  return (
    <div
      ref={viewerContainerRef}
      className={cn('h-full w-full overflow-hidden md:rounded-[16px]', className)}
    />
  );
}
